import React from 'react';
import DashboardLayout from '../../DashboardLayout';
import { PackagingOperationsProvider, usePackagingOperationsContext, type PKGOPS_State } from './context/PackagingOperationsContext';
import SetupComponent from './components/SetupComponent';
import ChecklistComponent from './components/ChecklistComponent';
import CorrectiveActionComponent from './components/CorrectiveActionComponent';
import ReverifyComponent from './components/ReverifyComponent';
import SummaryComponent from './components/SummaryComponent';

const PackagingOperationsApp: React.FC = () => {
    const { state } = usePackagingOperationsContext();

    const getStepperClasses = (stepState: PKGOPS_State, current: PKGOPS_State) => {
        const statesOrder: PKGOPS_State[] = ['SETUP', 'CHECKLIST_FILL', 'PENDING_PRODUCTION', 'PENDING_REVERIFICATION', 'COMPLETED'];
        const stepIndex = statesOrder.indexOf(stepState);
        const currentIndex = statesOrder.indexOf(current);

        if (stepIndex < currentIndex) {
            return "text-indigo-600 font-semibold border-b-2 border-indigo-600 pb-1"; // completed
        } else if (stepIndex === currentIndex) {
            return "text-blue-600 font-bold border-b-4 border-blue-600 pb-1"; // active
        } else {
            return "text-gray-400 font-medium"; // pending
        }
    };

    return (
        <DashboardLayout>
            <main id="main" className="main">
                <div className="container-fluid" style={{ padding: '20px' }}>
                    <div className="row">
                        <div className="col-12 text-center mb-4">
                            <h2 style={{ fontWeight: 'bold', color: '#1f2937' }}>PACKAGING OPERATIONS (Rajpura)</h2>
                            <p className="text-muted">AIB | Quality | Rajpura Plant</p>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12 max-w-7xl mx-auto space-y-6">
                            
                            {/* Header Bar */}
                            <div className="bg-white border border-slate-300 rounded-lg shadow-sm p-4 md:p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                <div>
                                    <h1 className="text-xl md:text-2xl font-extrabold text-slate-900 leading-tight m-0">Packaging Operations</h1>
                                    <div className="mt-2 text-sm text-slate-500 font-medium flex flex-wrap gap-x-4 gap-y-1">
                                        {state.currentState === 'SETUP' ? (
                                            <span>No active quality tour session.</span>
                                        ) : (
                                            <>
                                                <span><strong>Type:</strong> {state.sessionData.pkgopsType}</span>
                                                <span><strong>Line:</strong> {state.sessionData.lineNo}</span>
                                                <span><strong>Date:</strong> {state.sessionData.date}</span>
                                                <span><strong>QA Exec:</strong> {state.sessionData.qaExecutive}</span>
                                                <span><strong>Prod Exec:</strong> {state.sessionData.productionIncharge}</span>
                                                <span><strong>Status:</strong> <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded border border-yellow-200">{state.sessionData.status}</span></span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Stepper Progress Bar */}
                            <div className="bg-white border border-slate-300 rounded-lg shadow-sm p-4 md:p-5">
                                <div className="flex items-center justify-between flex-wrap gap-2 text-sm">
                                    <span className={getStepperClasses('SETUP', state.currentState)}>1. Setup Metadata</span>
                                    <span className="text-slate-300 font-bold">&gt;</span>
                                    <span className={getStepperClasses('CHECKLIST_FILL', state.currentState)}>2. QA Checklist</span>
                                    <span className="text-slate-300 font-bold">&gt;</span>
                                    <span className={getStepperClasses('PENDING_PRODUCTION', state.currentState)}>3. Production Action</span>
                                    <span className="text-slate-300 font-bold">&gt;</span>
                                    <span className={getStepperClasses('PENDING_REVERIFICATION', state.currentState)}>4. QA Re-Verify</span>
                                    <span className="text-slate-300 font-bold">&gt;</span>
                                    <span className={getStepperClasses('COMPLETED', state.currentState)}>5. Closed Summary</span>
                                </div>
                            </div>

                            {/* Main Content Area based on State */}
                            <div className="space-y-6">
                                {state.currentState === 'SETUP' && <SetupComponent />}
                                
                                {state.currentState === 'CHECKLIST_FILL' && <ChecklistComponent />}
                                
                                {state.currentState === 'PENDING_PRODUCTION' && (
                                    <>
                                        <CorrectiveActionComponent />
                                        <div className="opacity-60 pointer-events-none mt-4">
                                            <ChecklistComponent />
                                        </div>
                                    </>
                                )}
                                
                                {state.currentState === 'PENDING_REVERIFICATION' && (
                                    <>
                                        <ReverifyComponent />
                                        <div className="opacity-60 pointer-events-none mt-4">
                                            <CorrectiveActionComponent />
                                        </div>
                                    </>
                                )}

                                {state.currentState === 'COMPLETED' && <SummaryComponent />}
                            </div>

                        </div>
                    </div>
                </div>
            </main>
        </DashboardLayout>
    );
};

export default function RajpuraProductProductionOperations() {
    return (
        <PackagingOperationsProvider>
            <PackagingOperationsApp />
        </PackagingOperationsProvider>
    );
}
